import { cn } from 'cn';
import { ICON_PATHS, type IconName } from './icons';

/** The two drawn pieces the upload screen repeats: an icon and a status pill. */

export function Icon({
  name,
  size = 16,
  className,
}: {
  name: IconName;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={cn('shrink-0', className)}
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

const PILL_TONES = {
  queued: { className: 'bg-muted text-muted-foreground', icon: 'clock' },
  processing: { className: 'bg-status-ai-bg text-status-ai', icon: 'refresh' },
  done: { className: 'bg-success-bg text-success', icon: 'check' },
  failed: { className: 'bg-destructive/10 text-destructive', icon: 'alert' },
} satisfies Record<string, { className: string; icon: IconName }>;

/**
 * A status pill. Icon **and** word, never colour alone — the same rule the
 * provenance badges follow, and for the same reason.
 */
export function StatusPill({
  tone,
  children,
}: {
  tone: keyof typeof PILL_TONES;
  children: string;
}) {
  const { className, icon } = PILL_TONES[tone];
  return (
    <span
      className={cn(
        'inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full px-2.5',
        'text-[12px] font-semibold leading-none',
        className,
      )}
    >
      <Icon name={icon} size={13} />
      {children}
    </span>
  );
}
